import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { withTranslation } from "react-i18next";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  FormGroup,
  Label,
  Offcanvas,
  OffcanvasHeader,
  OffcanvasBody,
  Button,
} from "reactstrap";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import Breadcrumbs from "components/Common/Breadcrumb";
import ActionEdit from "components/buttons/actionEdit";
import SaveButton from "components/buttons/save";
import SafeField from "helpers/SafeField";
import EmailContentEditor from "components/Common/ContentEditor";
import { get, put, post } from "helpers/http_helper";

const normalizeList = payload => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.resultData)) return payload.resultData;
  if (Array.isArray(payload?.data?.resultData)) return payload.data.resultData;
  return [];
};

const emptyTemplate = {
  id: null,
  templateId: "",
  templateTitle: "",
  templateFilterTags: "",
  templateCategoryId: "",
  templateReplacementTag: "",
  templateEmailSubject: "",
  templateEmailBody: "",
  templateSMSBody: "",
  templateDescription: "",
  templateKeyword: "",
  isInternal: false,
  isSendEmailEnabled: true,
  isSendSMSEnabled: false,
  isActive: true,
};

/**
 * Action Plan Emails — ported from RS-agent /setting/webflow-emails/action-plan-emails
 * Uses AgentSystemNotificationTemplate APIs.
 */
const ActionPlanEmails = props => {
  document.title = "Action Plan Emails | Realty Space";

  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isOpen, setIsOpen] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [template, setTemplate] = useState(null);

  useEffect(() => {
    fetchAllTemplates();
  }, []);

  const fetchAllTemplates = () => {
    setLoading(true);
    get("AgentSystemNotificationTemplate/allAgent", {
      params: { agentId: localStorage.getItem("userId") },
    })
      .then(res => {
        setTemplates(normalizeList(res));
        setLoading(false);
      })
      .catch(() => {
        setTemplates([]);
        setLoading(false);
        toast.error("Failed to load action plan emails");
      });
  };

  const openAddNew = e => {
    if (e?.preventDefault) e.preventDefault();
    setTemplate({ ...emptyTemplate });
    setIsEdit(false);
    setIsOpen(true);
  };

  const openEdit = (e, tpl) => {
    if (e?.preventDefault) e.preventDefault();
    setTemplate({
      id: tpl.id,
      templateId: tpl.templateId || "",
      templateTitle: tpl.templateTitle || "",
      templateFilterTags: tpl.templateFilterTags || "",
      templateCategoryId: tpl.templateCategoryId ?? "",
      templateReplacementTag: tpl.templateReplacementTag || "",
      templateEmailSubject: tpl.templateEmailSubject || "",
      templateEmailBody: tpl.templateEmailBody || "",
      templateSMSBody: tpl.templateSMSBody || "",
      templateDescription: tpl.templateDescription || "",
      templateKeyword: tpl.templateKeyword || "",
      isInternal: tpl.isInternal ?? false,
      isSendEmailEnabled: tpl.isSendEmailEnabled ?? true,
      isSendSMSEnabled: tpl.isSendSMSEnabled ?? false,
      isActive: tpl.isActive ?? true,
    });
    setIsEdit(true);
    setIsOpen(true);
  };

  const closePanel = () => {
    setIsOpen(false);
    setIsEdit(false);
    setTemplate(null);
  };

  const handleSubmit = (values, { setSubmitting }) => {
    const data = {
      templateTitle: values.templateTitle,
      templateFilterTags: values.templateFilterTags,
      templateCategoryId: values.templateCategoryId
        ? Number(values.templateCategoryId)
        : null,
      templateReplacementTag: values.templateReplacementTag,
      templateEmailSubject: values.templateEmailSubject,
      templateEmailBody: values.templateEmailBody,
      templateSMSBody: values.templateSMSBody,
      templateDescription: values.templateDescription,
      templateKeyword: values.templateKeyword,
      agentId: parseInt(localStorage.getItem("userId"), 10) || 0,
      isInternal: values.isInternal,
      isSendEmailEnabled: values.isSendEmailEnabled,
      isSendSMSEnabled: values.isSendSMSEnabled,
      isActive: values.isActive,
    };

    if (isEdit && values.id) {
      data.id = values.id;
      if (values.templateId) data.templateId = values.templateId;
    }

    toast.loading("Please wait...");
    const request =
      isEdit && values.id
        ? put("AgentSystemNotificationTemplate", data)
        : post("AgentSystemNotificationTemplate", data);

    request
      .then(response => {
        toast.dismiss();
        if (response?.success !== false) {
          toast.success(
            isEdit
              ? "Email template updated successfully"
              : "Email template created successfully"
          );
          closePanel();
          fetchAllTemplates();
        } else {
          toast.error(
            response?.validationErrors?.[0] || "An error occurred."
          );
        }
      })
      .catch(() => {
        toast.dismiss();
        toast.error(isEdit ? "Update failed" : "Create failed");
      })
      .finally(() => setSubmitting(false));
  };

  const categoryLabel = id => {
    if (Number(id) === 1) return "Lead";
    if (Number(id) === 2) return "Client";
    return "—";
  };

  return (
    <React.Fragment>
      {loading ? <div className="loading">Loading…</div> : null}
      <ToastContainer autoClose={2000} />

      <div className="page-content">
        <Container fluid>
          <Row className="mb20 align-items-center">
            <Col sm="7">
              <Breadcrumbs
                title={props.t("General Settings")}
                breadcrumbItem={props.t("Action Plan Emails")}
              />
            </Col>
            <Col sm="5">
              <div className="mt-3 float-end">
                <Button color="primary" onClick={openAddNew}>
                  {props.t("Add New")}
                </Button>
              </div>
            </Col>
          </Row>

          <Row>
            <Col xl="12">
              <Card>
                <CardBody>
                  {(templates || []).length === 0 && !loading ? (
                    <p className="text-muted mb-0">
                      No action plan email templates yet. Click{" "}
                      <strong>Add New</strong> to create one.
                    </p>
                  ) : (
                    <div className="table-responsive">
                      <table className="table table-bordered align-middle mb-0">
                        <thead className="table-light">
                          <tr>
                            <th>Template Name</th>
                            <th style={{ width: 140 }}>Category</th>
                            <th style={{ width: 100 }}>Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(templates || []).map((tpl, index) => (
                            <tr key={tpl.id || tpl.templateId || index}>
                              <td>{tpl.templateTitle}</td>
                              <td>{categoryLabel(tpl.templateCategoryId)}</td>
                              <td>
                                <ActionEdit
                                  onClick={e => openEdit(e, tpl)}
                                />
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>

      <Offcanvas
        isOpen={isOpen}
        direction="end"
        toggle={closePanel}
        style={{ width: 600 }}
      >
        <OffcanvasHeader toggle={closePanel}>
          {isEdit ? "Update Email Template" : "Add New Email"}
        </OffcanvasHeader>
        <OffcanvasBody>
          <Formik
            enableReinitialize
            initialValues={template || emptyTemplate}
            validationSchema={Yup.object({
              templateTitle: Yup.string().required("Template name is required"),
              templateEmailSubject: Yup.string().required(
                "Email subject is required"
              ),
              templateCategoryId: Yup.string().required("Category is required"),
            })}
            onSubmit={handleSubmit}
          >
            {({ errors, touched, values, handleChange, setFieldValue, isSubmitting }) => (
              <Form className="needs-validation">
                <FormGroup className="mb-3">
                  <div className="form-check">
                    <Field
                      type="checkbox"
                      name="isSendEmailEnabled"
                      className="form-check-input"
                      id="isSendEmailEnabled"
                    />
                    <Label
                      className="form-check-label"
                      htmlFor="isSendEmailEnabled"
                    >
                      Active
                    </Label>
                  </div>
                </FormGroup>

                <FormGroup className="mb-3">
                  <Label htmlFor="templateTitle">Template name*</Label>
                  <SafeField
                    name="templateTitle"
                    className={
                      "form-control" +
                      (errors.templateTitle && touched.templateTitle
                        ? " is-invalid"
                        : "")
                    }
                    onChange={handleChange}
                    type="text"
                    placeholder="e.g. Welcome Email"
                  />
                  <ErrorMessage
                    name="templateTitle"
                    component="div"
                    className="invalid-feedback"
                  />
                </FormGroup>

                <FormGroup className="mb-3">
                  <Label htmlFor="templateCategoryId">Category</Label>
                  <Field
                    as="select"
                    name="templateCategoryId"
                    className={
                      "form-control" +
                      (errors.templateCategoryId && touched.templateCategoryId
                        ? " is-invalid"
                        : "")
                    }
                  >
                    <option value="">Select category</option>
                    <option value="1">Lead</option>
                    <option value="2">Client</option>
                  </Field>
                  <ErrorMessage
                    name="templateCategoryId"
                    component="div"
                    className="invalid-feedback"
                  />
                </FormGroup>

                <FormGroup className="mb-3">
                  <Label htmlFor="templateEmailSubject">Email subject*</Label>
                  <SafeField
                    name="templateEmailSubject"
                    className={
                      "form-control" +
                      (errors.templateEmailSubject &&
                      touched.templateEmailSubject
                        ? " is-invalid"
                        : "")
                    }
                    onChange={handleChange}
                    type="text"
                  />
                  <ErrorMessage
                    name="templateEmailSubject"
                    component="div"
                    className="invalid-feedback"
                  />
                </FormGroup>

                <FormGroup className="mb-3">
                  <Label htmlFor="templateEmailBody">Email body</Label>
                  <EmailContentEditor
                    name="templateEmailBody"
                    setFieldValue={setFieldValue}
                    initialContent={values.templateEmailBody}
                  />
                </FormGroup>

                <SaveButton disabled={isSubmitting}>Save</SaveButton>
              </Form>
            )}
          </Formik>
        </OffcanvasBody>
      </Offcanvas>
    </React.Fragment>
  );
};

ActionPlanEmails.propTypes = {
  t: PropTypes.any,
};

export default withTranslation()(ActionPlanEmails);
